/**
 * Stored lesson content (markdown + this editor's tag scheme + $…$ math) ->
 * HTML for the mobile apps' KaTeX WebView (KatexHtmlView). The web app
 * renders the same content with React (components/RenderContent.jsx); both
 * share marks.js/blocks.js, so they agree on math spans, legacy <eq>, and
 * the palette.
 *
 * SHARED SOURCE: copied into teacher-mobile and student-mobile by
 * scripts/sync-rich-content.js — edit here, then run that script.
 */

import { legacyEqToMath } from './marks.js';
import { TEXT_COLOR_HEX, BG_COLOR_HEX } from './blocks.js';

export const MATH_TOKEN_RE = /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([^\n]+?\\\)|\$[^\n$]+?\$)/g;

// The editor backslash-escapes marker characters it means literally
// (\* \` \~ \< \\) and AI-written markdown uses \$ for a real dollar sign.
// Those pairs are swapped for private-use placeholders before math splitting
// / markdown conversion, so they're never mistaken for markers or math
// delimiters, then restored: as the plain character in prose, and as the
// original LaTeX in math (except \<, which KaTeX rejects).
const ESCAPABLE = '\\*`~<$';
const PLACEHOLDER_BASE = 0xe000;
const ESCAPED_PAIR_RE = /\\([\\*`~<$])/g;
const PLACEHOLDER_RE = /[-]/g;

// LaTeX commands with no $…$ around them at all (terse card/flashcard
// answers like "\vec{F} = m\vec{a}").
const BARE_LATEX_RE = /\\[a-zA-Z]{2,}|\^\{|_\{/;

export function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// One normalisation for math source on web and mobile:
//  - KaTeX has no \mspace (amsmath) but supports \mskip with the same argument;
//  - over-escaped commands (`\\frac`, from double-escaped generation) are
//    collapsed to `\frac`, but only outside \begin…\end environments, where
//    `\\` is a real line break (aligned, matrix, cases).
export function normalizeLatex(latex) {
  const source = String(latex).replace(/\\mspace(?![a-zA-Z])/g, '\\mskip');
  if (source.includes('\\begin{')) return source;
  return source.replace(/\\\\(?=[a-zA-Z])/g, '\\');
}

function protectEscapes(s) {
  return s.replace(ESCAPED_PAIR_RE, (_m, c) => String.fromCharCode(PLACEHOLDER_BASE + ESCAPABLE.indexOf(c)));
}

function restoreEscapes(s, inMath) {
  return s.replace(PLACEHOLDER_RE, (ph) => {
    const c = ESCAPABLE[ph.charCodeAt(0) - PLACEHOLDER_BASE];
    // A prose dollar gets its own element: KaTeX auto-render scans each text
    // node separately, so it can't pair this "$" with a math delimiter.
    if (!inMath) return c === '$' ? '<span>$</span>' : escapeHtml(c);
    return c === '<' ? '&lt;' : `\\${escapeHtml(c)}`;
  });
}

/** Cheap presence check so callers can skip the WebView for plain text. */
export function hasMathOrLatex(text) {
  if (!text) return false;
  return /\$|\\\(|\\\[|<eqb?>|&lt;eqb?&gt;/.test(text) || BARE_LATEX_RE.test(text);
}

/** The editor's own inline tag scheme (<b>/<i>/<u>/<s>/<code>/<c:x>/<hl:x>). */
export function hasEditorTags(text) {
  if (!text) return false;
  return /<\/?(?:b|i|u|s|code)>|<(?:c|hl):[a-z]+>/.test(text);
}

/** Broader than hasMathOrLatex: also true for markdown that needs HTML
 * conversion (bold/italic/code/lists/headings/quotes). */
export function hasRichFormatting(text) {
  if (!text) return false;
  if (hasMathOrLatex(text) || hasEditorTags(text)) return true;
  return /\*\*|\*[^*\n]+\*|`[^`\n]+`|^#{1,4}\s|^[-*]\s|^\d+\.\s|^>\s/m.test(text);
}

function proseToHtml(part) {
  return escapeHtml(part)
    .replace(/&lt;\/b&gt;/g, '</b>').replace(/&lt;b&gt;/g, '<b>')
    .replace(/&lt;\/i&gt;/g, '</i>').replace(/&lt;i&gt;/g, '<i>')
    .replace(/&lt;\/u&gt;/g, '</u>').replace(/&lt;u&gt;/g, '<u>')
    .replace(/&lt;\/s&gt;/g, '</s>').replace(/&lt;s&gt;/g, '<s>')
    .replace(/&lt;\/code&gt;/g, '</code>').replace(/&lt;code&gt;/g, '<code>')
    .replace(/&lt;\/c&gt;/g, '</span>')
    .replace(/&lt;c:([a-z]+)&gt;/g, (_m, key) => `<span style="color:${TEXT_COLOR_HEX[key] || 'inherit'}">`)
    .replace(/&lt;\/hl&gt;/g, '</span>')
    .replace(/&lt;hl:([a-z]+)&gt;/g, (_m, key) => `<span style="background:${BG_COLOR_HEX[key] || 'transparent'};border-radius:3px;padding:0 2px;">`)
    // Legacy bare markdown (AI-written content).
    .replace(/\*\*\*([^*\n]+)\*\*\*/g, '<b><i>$1</i></b>')
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/\*([^*\n]+)\*/g, '<i>$1</i>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>');
}

/** One line/paragraph's inline content -> HTML. */
export function inlineToHtml(raw) {
  if (!raw) return '';
  let normalized = legacyEqToMath(raw.replace(/&lt;(\/?)(eqb?)&gt;/g, '<$1$2>'));
  if (!/\$|\\\(|\\\[/.test(normalized) && BARE_LATEX_RE.test(normalized)) normalized = `$${normalized}$`;

  return protectEscapes(normalized)
    .split(MATH_TOKEN_RE)
    .map((part, i) => (i % 2 === 1
      ? restoreEscapes(escapeHtml(normalizeLatex(part)), true)
      : restoreEscapes(proseToHtml(part), false)))
    .join('');
}

function detectLine(line) {
  let m;
  if ((m = line.match(/^####\s+(.*)$/))) return { type: 'h4', rest: m[1] };
  if ((m = line.match(/^###\s+(.*)$/))) return { type: 'h3', rest: m[1] };
  if ((m = line.match(/^##\s+(.*)$/))) return { type: 'h2', rest: m[1] };
  if ((m = line.match(/^#\s+(.*)$/))) return { type: 'h1', rest: m[1] };
  if ((m = line.match(/^>\s+(.*)$/))) return { type: 'quote', rest: m[1] };
  if ((m = line.match(/^[-*]\s+(.*)$/))) return { type: 'bullet', rest: m[1] };
  if ((m = line.match(/^\d+\.\s+(.*)$/))) return { type: 'numbered', rest: m[1] };
  return { type: 'p', rest: line };
}

/** Full section content -> HTML body: consecutive plain lines join into one
 * <p> (like web's split on blank lines), bullets/numbers group into lists. */
export function blocksToHtml(content) {
  const lines = (content ?? '').split('\n');
  let html = '';
  let listOpen = null;
  let paraBuffer = [];

  const closeList = () => { if (listOpen) { html += `</${listOpen}>`; listOpen = null; } };
  const flushPara = () => {
    if (paraBuffer.length) { html += `<p>${inlineToHtml(paraBuffer.join(' '))}</p>`; paraBuffer = []; }
  };

  for (const line of lines) {
    if (!line.trim()) { flushPara(); closeList(); continue; }
    const { type, rest } = detectLine(line);
    if (type === 'p') { paraBuffer.push(rest); continue; }

    flushPara();
    const inner = inlineToHtml(rest);
    if (type === 'bullet' || type === 'numbered') {
      const tag = type === 'bullet' ? 'ul' : 'ol';
      if (listOpen !== tag) { closeList(); html += `<${tag}>`; listOpen = tag; }
      html += `<li>${inner}</li>`;
      continue;
    }
    closeList();
    html += type === 'quote' ? `<blockquote>${inner}</blockquote>` : `<${type}>${inner}</${type}>`;
  }
  flushPara();
  closeList();
  return html;
}
